/** Strips the "name: v" prefix the version string carries, leaving the bare version. */
export function parseBioscopeVersion(v: string | null | undefined): string | null {
    if (!v) {
        return null;
    }

    return v.replace(/.*: v?/g, '');
}

// Compare a version section fragment, e.g. 1.2.3-beta is split into the
// fragments 1.2.3 and beta
function cmpFragment(lhs: string, rhs: string): number {
    const isDigit = (char: string): boolean => /^\d$/.test(char);
    const isAlpha = (char: string): boolean => /^[A-Z]$/i.test(char);
    // Rank a single character with a value for ordering
    const order = (character: string): number => {
        if (isDigit(character)) return 0;
        if (isAlpha(character)) return character.charCodeAt(0);
        if (character === '~') return -1;
        if (character !== '\0') return character.charCodeAt(0) + 256;
        return 0;
    };
    let lhs_index = 0;
    let rhs_index = 0;
    while (lhs_index !== lhs.length && rhs_index !== rhs.length) {
        let first_diff = 0;
        // First compare leading letters, if any
        while (
            lhs_index !== lhs.length &&
            rhs_index !== rhs.length &&
            (!isDigit(lhs[lhs_index]) || !isDigit(rhs[rhs_index]))
        ) {
            const lhs_value = order(lhs[lhs_index]);
            const rhs_value = order(rhs[rhs_index]);
            if (lhs_value !== rhs_value) return lhs_value - rhs_value;
            lhs_index++;
            rhs_index++;
        }
        // Ignore leading 0s
        while (lhs_index < lhs.length && lhs[lhs_index] === '0') lhs_index++;
        while (rhs_index < rhs.length && rhs[rhs_index] === '0') rhs_index++;
        if (rhs_index >= rhs.length || lhs_index >= lhs.length) break;
        // Iterate through numbers
        while (
            isDigit(lhs[lhs_index]) &&
            isDigit(rhs[rhs_index]) &&
            lhs_index < lhs.length &&
            rhs_index < rhs.length
        ) {
            if (first_diff === 0) {
                first_diff = lhs.charCodeAt(lhs_index) - rhs.charCodeAt(rhs_index);
            }
            lhs_index++;
            rhs_index++;
        }
        // If we still have digits to consume here, that means lhs is a longer
        // number than rhs, and therefore bigger
        if (lhs_index < lhs.length && isDigit(lhs[lhs_index])) return 1;
        if (rhs_index < rhs.length && isDigit(rhs[rhs_index])) return -1;
        // Both numbers are equally long, return first diff if any
        if (first_diff !== 0) return first_diff;
    }
    // We've consumed at least one of the fragments, both leading letters and
    // the number itself

    // Both versions at the end with no difference found, must be equal
    if (lhs_index >= lhs.length && rhs_index >= rhs.length) return 0;

    // Lhs is shorter
    if (lhs_index >= lhs.length) {
        // Versions are the same, but rhs ends with ~, so is considered smaller
        if (rhs[rhs_index] === '~') return 1;
        return -1;
    }
    if (rhs_index >= rhs.length) {
        if (lhs[lhs_index] === '~') return -1;
        return 1;
    }
    // This should never happen
    return Number.NaN;
}

// Compare versions the same way Debian does for packages
export function cmpVersion(lhs: string, rhs: string): number {
    const NOT_FOUND = -1;
    const lhs_epoch_index = lhs.indexOf(':');
    const rhs_epoch_index = rhs.indexOf(':');
    // Found at least one epoch
    if (lhs_epoch_index !== NOT_FOUND || rhs_epoch_index !== NOT_FOUND) {
        // Leading 0s are ignored, and a 0 epoch is treated as no epoch
        const lhs_epoch = lhs.slice(lhs_epoch_index).replace(/^0*/g, '');
        const rhs_epoch = rhs.slice(rhs_epoch_index).replace(/^0*/g, '');
        const res = cmpFragment(lhs_epoch, rhs_epoch);
        if (res !== 0) return res;
    }
    // Same epoch, strip it
    lhs = lhs_epoch_index !== NOT_FOUND ? lhs.substr(0, lhs_epoch_index) : lhs;
    rhs = rhs_epoch_index !== NOT_FOUND ? rhs.substr(0, rhs_epoch_index) : rhs;

    // Find the last -, we want to compare these fragments separately
    const lhs_revision_index = lhs.lastIndexOf('-');
    const rhs_revision_index = rhs.lastIndexOf('-');
    const lhs_main = lhs_revision_index !== NOT_FOUND ? lhs.substring(0, lhs_revision_index) : lhs;
    const rhs_main = rhs_revision_index !== NOT_FOUND ? rhs.substring(0, rhs_revision_index) : rhs;
    let res = cmpFragment(lhs_main, rhs_main);
    if (res !== 0) return res;

    // Main version is the same, so compare the revision
    // No revision is treated like revision 0
    const lhs_revision = lhs_revision_index !== NOT_FOUND ? lhs.substring(lhs_revision_index) : '0';
    const rhs_revision = rhs_revision_index !== NOT_FOUND ? rhs.substring(rhs_revision_index) : '0';
    res = cmpFragment(lhs_revision, rhs_revision);
    return res;
}
